import axios from "axios";

const isDraftPreview = window.location.hostname === "beteiligung.steg-gis.de";
export const RootURL = isDraftPreview
  ? "/preview-api/"
  : "https://backend.steg-gis.de/cms/";

const cmsAPI = axios.create({
  baseURL: RootURL,
  headers: { "Content-Type": "application/json" },
});

export const isAuthenticatedPreview = window.location.pathname.startsWith("/cms-preview/") || new URLSearchParams(window.location.search).get("cms_preview") === "1";
if (isAuthenticatedPreview) {
  const allowedOrigins = [window.location.origin, "http://localhost:5178", "http://localhost:5182", "https://portal.steg-gis.de"];
  let pendingCredentials: Promise<string> | null = null;
  const credentials = (refresh = false): Promise<string> => {
    if (pendingCredentials) return pendingCredentials;
    pendingCredentials = new Promise<string>((resolve, reject) => {
      const cleanup = () => { clearTimeout(timer); window.removeEventListener("message", receive); };
      function receive(event: MessageEvent) {
        if (event.source !== window.parent || !allowedOrigins.includes(event.origin)) return;
        if (event.data?.type === "cms-preview-auth-error") {
          cleanup(); reject(new Error("Vorschau-Anmeldung abgelaufen. Bitte erneut anmelden."));
        } else if (event.data?.type === "cms-preview-auth" && typeof event.data.token === "string") {
          cleanup(); resolve(event.data.token);
        }
      }
      const timer = window.setTimeout(() => { cleanup(); reject(new Error("Vorschau-Anmeldung fehlt")); }, 15000);
      window.addEventListener("message", receive);
      allowedOrigins.forEach(origin => window.parent.postMessage({ type: "cms-preview-ready", refresh }, origin));
    }).finally(() => { pendingCredentials = null; });
    return pendingCredentials;
  };
  cmsAPI.interceptors.request.use(async config => {
    config.headers.Authorization = `Bearer ${await credentials()}`;
    config.params = { ...config.params, cms_preview: "1" };
    if (config.method && !["get", "head", "options"].includes(config.method) && !(config.method === "post" && config.url === "map-comments/")) throw new Error("Die Vorschau ist schreibgeschützt.");
    return config;
  });
  cmsAPI.interceptors.response.use(response => response, async error => {
    const config = error.config;
    if (error.response?.status !== 401 || !config || config._previewRetry) return Promise.reject(error);
    config._previewRetry = true;
    await credentials(true);
    return cmsAPI(config);
  });
}

export interface Asset {
  id: number;
  title: string;
  description?: string;
  file: string;
  alt_text?: string;
  caption?: string;
  source?: string;
}

export interface Block {
  id: number;
  blockType: string;
  layoutType: string;
  title?: string;
  content?: string;
  assets: Asset[];
  order: number;
  settings?: any;
  items?: any[];
}

export interface MapComment {
  id: number;
  title: string;
  comment: string;
  category: string;
  geom: { type: "Point"; coordinates: [number, number] };
}

export type ContactSubmission = {
  site: number; first_name: string; last_name: string; email: string;
  company_name?: string; address?: string; title: string; message: string; rules_accepted: boolean;
  website?: string;
};

export type ContactSubmissionResult = {
  detail: string;
  reference_id?: string;
  referenceId?: string;
};

export const submitContactForm = async (submission: ContactSubmission): Promise<ContactSubmissionResult> => {
  return (await cmsAPI.post<ContactSubmissionResult>("contact-submissions/", submission)).data;
};

export interface PageContent {
  id: number;
  title: string;
  slug: string;
  urlKey?: string;
  url_key?: string;
  is_published?: boolean;
  isPublished?: boolean;
  headerImage?: string;
  blocks: Block[];
}

export interface Site {
  id: number;
  title: string;
  description?: string;
  urlKey: string;
  instanceName?: string;
  instanceUrlKey?: string;
  schemaName?: string;
  gemeindeName?: string;
  projektId?: number;
  // The public CMS currently exposes this misspelled field.
  projecktId?: string | number;
  projectNummer?: string;
  project_nummer?: string;
  project?: any;
  isActive?: boolean;
  is_active?: boolean;
  isPublished?: boolean;
  is_published?: boolean;
  logo?: string;
  headerImage?: string;
  primaryColor?: string;
  footerText?: string;
  knowledge_search_enabled?: boolean;
  knowledgeSearchEnabled?: boolean;
  contact_privacy_text?: string;
  hasContactForm?: boolean;
  has_contact_form?: boolean;
  projectLeader?: number | null;
}

export interface MenuItem {
  id: number;
  parent: number | null;
  page: number | null;
  title: string;
  urlKey: string;
  children: MenuItem[];
  order: number;
  isActive?: boolean;
  isVisible?: boolean;
}

export interface SiteTeamMember {
  id: number;
  employeeId?: number | null;
  name: string;
  full_name?: string;
  position?: string;
  organisation?: string;
  address?: string;
  phone?: string;
  email?: string;
  image?: string;
  type: string;
  order: number;
}

/** Public published pages for one site. */
export const getPublicPages = async (siteId: number): Promise<PageContent[]> =>
  (await cmsAPI.get<PageContent[]>(`${isAuthenticatedPreview ? "admin/" : ""}pages/`, { params: { site: siteId } })).data;

/** Public published CMS sites. Safe for visitors without a login. */
export const getPublicSites = async (): Promise<Site[]> =>
  (await cmsAPI.get<Site[]>("sites/")).data;

export const adminGetSite = async (
  identifier: number | string,
  mandant?: string
): Promise<Site> =>
  (
    await cmsAPI.get<Site>(`admin/sites/${encodeURIComponent(identifier)}/`, {
      params: mandant ? { mandant } : undefined,
    })
  ).data;

export const adminGetSiteTabs = async (
  identifier: number | string,
  mandant?: string
): Promise<MenuItem[]> =>
  (
    await cmsAPI.get<MenuItem[]>(
      `admin/sites/${encodeURIComponent(identifier)}/tabs/`,
      { params: mandant ? { mandant } : undefined }
    )
  ).data;

export const getPublicSiteByUrlKey = async (urlKey: string): Promise<Site> => {
  if (isAuthenticatedPreview && /^site-\d+$/.test(urlKey)) return adminGetSite(urlKey);
  return (await cmsAPI.get<Site>(`sites/by-url-key/${encodeURIComponent(urlKey)}/`)).data;
};

export const getPublicSiteByProjectNumber = async (
  projectNumber: number | string
): Promise<Site> =>
  (
    await cmsAPI.get<Site>(
      isAuthenticatedPreview ? `admin/sites/${encodeURIComponent(projectNumber)}/` : `sites/by-project-number/${encodeURIComponent(projectNumber)}/`
    )
  ).data;

export const getPublicSiteNavigation = async (
  siteId: number | string
): Promise<MenuItem[]> =>
  (await cmsAPI.get<MenuItem[]>(isAuthenticatedPreview ? `admin/sites/site-${encodeURIComponent(siteId)}/tabs/` : `sites/${encodeURIComponent(siteId)}/navigation/`))
    .data;

export const getPublicPage = async (
  pageId: number | string
): Promise<PageContent> =>
  (await cmsAPI.get<PageContent>(isAuthenticatedPreview ? `admin/pages/${encodeURIComponent(pageId)}/` : `pages/by-id/${encodeURIComponent(pageId)}/`))
    .data;

/** Published map contributions for one map block. */
export const getPublicMapComments = async (
  blockId: number | string
): Promise<MapComment[]> =>
  (await cmsAPI.get<MapComment[]>("map-comments/", { params: { block: blockId } })).data;

export const adminGetPage = async (
  identifier: number | string
): Promise<PageContent> =>
  (
    await cmsAPI.get<PageContent>(
      `admin/pages/${encodeURIComponent(identifier)}/`
    )
  ).data;

export const adminGetPublicTeamMembers = async (
  site: number | string,
  block?: number
): Promise<SiteTeamMember[]> =>
  (
    await cmsAPI.get<SiteTeamMember[]>(isAuthenticatedPreview ? "admin/site-team-members/" : `sites/${site}/team/`, {
      params: { ...(block ? { block } : {}), ...(isAuthenticatedPreview ? { site } : {}) },
    })
  ).data;

export const submitMapComment = async (block: number, point: { title: string; comment: string; category: string; geom: { type: "Point"; coordinates: [number, number] } }): Promise<{ detail: string }> =>
  (await cmsAPI.post("map-comments/", { block, ...point })).data;
